/**
 * localStorage-backed notification layer.
 * Used for cross-tenant events that don't yet have a backend endpoint
 * (e.g. rep access requests). Stored per recipient tenantId.
 */

const KEY = 'local_notifications';

export interface LocalNotification {
  id: string;
  tenantId: number;       // recipient tenant
  type: string;
  title: string;
  message: string;
  senderTenantName?: string;
  isRead: boolean;
  link?: string;          // navigate-to path on click
  createdAt: string;
}

function load(): LocalNotification[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as LocalNotification[];
  } catch {
    return [];
  }
}

function save(items: LocalNotification[]) {
  localStorage.setItem(KEY, JSON.stringify(items));
}

/** Add a notification for a specific tenant. */
export function pushLocalNotification(n: Omit<LocalNotification, 'id' | 'isRead' | 'createdAt'>) {
  const all = load();
  all.unshift({
    ...n,
    id: `ln-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    isRead: false,
    createdAt: new Date().toISOString(),
  });
  save(all);
}

/** Get all notifications for a tenant (most recent first). */
export function getLocalNotifications(tenantId: number): LocalNotification[] {
  return load().filter(n => n.tenantId === tenantId);
}

/** Count unread for a tenant. */
export function getLocalUnreadCount(tenantId: number): number {
  return load().filter(n => n.tenantId === tenantId && !n.isRead).length;
}

/** Mark one as read. */
export function markLocalRead(id: string) {
  const all = load().map(n => n.id === id ? { ...n, isRead: true } : n);
  save(all);
}

/** Mark all for a tenant as read. */
export function markAllLocalRead(tenantId: number) {
  const all = load().map(n => n.tenantId === tenantId ? { ...n, isRead: true } : n);
  save(all);
}
